// Market Flow: index picker, replay, the flow board, top movers, the shortlist and how to read it.
import { Link } from '@/lib/nav';
import { useRouter, useSearchParams } from '@/lib/nav';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { apiUrl } from '@/lib/api';
import { 
  ArrowRightIcon, BarChart3Icon, ClockIcon, Maximize2Icon, Minimize2Icon, BoxIcon, GaugeIcon, LayersIcon, LightbulbIcon, LineChartIcon, ListOrderedIcon,
  RefreshCwIcon, ShuffleIcon, StarIcon, TriangleIcon, WavesIcon, XIcon,
} from 'lucide-react';
import { NSE, NSE_NAMES, detail, forSector, sessionPhase, snapshot, zoneState, type Analysed, type Session, type Snapshot } from '@planb/core';
import { fmtPct, renderBoard, type BoardUi } from '@/charts/board';
import { drawStockChart } from '@/charts/stock-chart';
import { since, useJournal } from '@/lib/client/use-journal';
import { lastBarForming, todayIso, useIndexStrip, useMarket, type FeedStatus, type Mode } from '@/lib/client/use-market';
import { MOVER_LISTS, moverList, type MoverList } from '@/lib/client/movers';
import { useMe, useResolvedTheme } from '@/lib/client/use-me';
import { cn } from '@/lib/utils';
import { HolidayTag } from './holiday-tag';
import { TurnHint, useFullscreen } from '@/lib/fullscreen';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AppHeader, Page } from './app-header';
import { IndexSelect, carry, dateSeed, indexById, stockPath, story } from './common';
import { Change, Initials, Logo, Pills, Sparkline, DeskIcon } from './market-ui';
import { refreshRecents, useRecents } from '@/lib/recents';
import { MoversTable } from './movers-table';
import { GUIDE_START, openDesk } from './desk';
import { usePageTools } from '@/lib/desk/tools';
import { usePaperTrades } from './paper-trades';
import { ReplayBar, useReplay } from './replay';

const cur = NSE.currencySymbol;
const fmtPx = NSE.formatPrice;

interface Tip { x: number; y: number; s: Analysed; rank: number }

export function BoardApp() {
  const router = useRouter();
  const params = useSearchParams();
  const [index, setIndex] = useState(indexById(params.get('index'))?.id ?? 'FNO');
  // Without ?index=, open on the member's default index once their preferences load.
  const prefIndex = useMe()?.prefs.index;
  const usedPref = useRef(false);
  useEffect(() => {
    if (usedPref.current || !prefIndex || params.get('index') || !indexById(prefIndex)) return;
    usedPref.current = true;
    setIndex(prefIndex);
  }, [prefIndex, params]);
  const [sector, setSector] = useState<string | null>(params.get('sector'));
  const [seed, setSeed] = useState(Number(params.get('seed')) || dateSeed());
  const [explain, setExplain] = useState(false);
  const [list, setList] = useState<MoverList>('bulls');
  const [tip, setTip] = useState<Tip | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // Full screen, landscape on phones (lib/fullscreen.ts); a tooltip doesn't survive the change.
  const full = useFullscreen<HTMLDivElement>(() => setTip(null));
  const fsRef = full.ref, fs = full.fs;
  const boxRef = useRef<HTMLDivElement>(null);
  // In full screen the board is laid out at the screen's aspect ratio (1190 design units tall).
  const [boardWidth, setBoardWidth] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = boxRef.current;
    if (!fs || !el) { setBoardWidth(undefined); return; }
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) setBoardWidth(Math.min(3200, Math.max(1490, Math.round((1190 * width) / height))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fs]);

  const { session, status, mode, live, version, user, role } = useMarket({ index, seed });
  const me = useMe();
  const { rows: paperRows } = usePaperTrades();
  const firstTimer = !!me?.access && paperRows !== null && paperRows.length === 0;
  // The morning reminder links to /?kite=login: continue straight to the broker login (admin only).
  const kiteIntent = useRef(params.get('kite') === 'login');
  useEffect(() => {
    if (!kiteIntent.current || !role || !status) return;
    kiteIntent.current = false;
    if (role !== 'admin') return;
    if (status.loggedIn) toast.success('Kite is already connected');
    else if (status.brokerConfigured) location.assign(apiUrl('/v1/kite/login'));
  }, [role, status]);
  // The API's Kite callback reports back with ?kite=failed / cancelled / unavailable.
  useEffect(() => {
    const k = params.get('kite');
    if (k === 'failed') toast.error('Kite login didn’t complete. Try again; if it keeps failing, check the API key and secret.');
    else if (k === 'cancelled') toast.message('Kite login was cancelled.');
    else if (k === 'unavailable') toast.error('Kite isn’t configured on the server.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const strip = useIndexStrip(session, live, version);
  const journal = useJournal();
  const findEntry = journal.find; // stable until entries change, unlike the `journal` object
  const ix = indexById(index)!;
  const date = session?.date || todayIso();

  const board = useMemo(() => {
    if (!session) return null;
    const members = new Set(ix.members);
    return { ...session, stocks: session.stocks.filter((s) => members.has(s.symbol)) };
    // `version` bumps when streamed bars mutate the session in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, ix, version]);
  const replay = useReplay(board?.stocks[0]?.bars.length ?? 0);
  const theme = useResolvedTheme(); // charts redraw in the new colours
  const tt = replay.tt;
  const forming = lastBarForming(mode, tt, replay.n);
  const snap = useMemo(() => (board && tt ? snapshot(NSE, board, tt, { lastBarForming: forming }) : null), [board, tt, forming]);
  const activeSector = sector && snap?.sectors.some((s) => s.name === sector) ? sector : null;
  const view = useMemo(() => (snap && activeSector ? forSector(NSE, snap, activeSector) : snap), [snap, activeSector]);
  const bySymbol = useMemo(() => new Map((snap?.stocks ?? []).map((s) => [s.symbol, s])), [snap]);
  const query = carry({ index, live, seed });

  // Keep the URL shareable: index, sector and (simulated) seed.
  useEffect(() => {
    const q = new URLSearchParams(query.slice(1));
    if (activeSector) q.set('sector', activeSector);
    router.replace(q.size ? `/?${q}` : '/', { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, activeSector]);

  const stockHref = useCallback((sym: string) => stockPath(sym, query), [query]);

  const highlight = useCallback((sym: string, on: boolean) => {
    svgRef.current?.querySelectorAll(`[data-sym="${CSS.escape(sym)}"]`).forEach((el) => {
      if (el.classList.contains('card')) return;
      el.classList.toggle('hl', on);
      if (on) el.parentNode?.appendChild(el);
    });
  }, []);

  useEffect(() => {
    if (!svgRef.current || !view || !snap) return;
    const ui: BoardUi = {
      t: () => tt,
      isShortlisted: (sym) => !!findEntry(date, sym),
      selectSector: (name) => { setSector(name); setTip(null); },
      highlight,
      tooltip: (ev, s, rank) => setTip(ev && s && rank ? { x: ev.clientX, y: ev.clientY, s, rank } : null),
      openStock: (sym, ev) => {
        const href = stockHref(sym);
        if (ev && (ev.metaKey || ev.ctrlKey || ('button' in ev && ev.button === 1))) window.open(href, '_blank');
        else router.push(href);
      },
    };
    // A redraw replaces every element, so the hovered card never gets its mouseleave: drop the tooltip.
    setTip(null);
    renderBoard(svgRef.current, NSE, view, ui, { sectors: snap.sectors, sector: activeSector, width: boardWidth });
  }, [view, snap, activeSector, tt, findEntry, date, highlight, stockHref, router, boardWidth, theme]);

  // The desk's view of this page: the board as shown (index, sector filter, time), sectors ranked,
  // the bullish/bearish count and the strongest stocks on each side.
  usePageTools({
    read_page: () => {
      if (!snap || !view) return { page: 'market-flow', note: 'still loading' };
      const n2 = (x: number) => +x.toFixed(2);
      const row = (x: (typeof view.stocks)[number]) => ({ symbol: x.symbol, sector: x.sector, changePct: n2(x.pct), strength: x.score, volumeVsUsual: `${x.relVol.toFixed(2)}×`, futuresBuildUp: x.hasOI ? x.buildUp : null, withSector: x.withSector });
      return {
        page: 'market-flow', index: ix.name, sector: activeSector ?? 'all sectors', session: date, asOf: snap.time,
        data: live ? (mode === 'closed' ? 'real NSE data, market closed' : 'real NSE data, live') : 'loading',
        bullish: view.bulls.length, bearish: view.bears.length,
        sectorsStrongestFirst: snap.sectors.map((x) => ({ sector: x.name, avgMovePct: n2(x.avg), buyersInControl: `${x.adv} of ${x.n}` })),
        strongestBullish: view.topBulls.slice(0, 10).map(row), strongestBearish: view.topBears.slice(0, 10).map(row),
        note: 'The Market Flow board: what is moving today. These are not tested signals (MoneyHQ\'s intraday tests found no edge after costs).',
      };
    },
  });

  // Esc clears the sector filter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && activeSector) setSector(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeSector]);

  // Full screen: the board alone, fitted to the screen. F toggles; Esc (browser) exits.
  const toggleFs = full.toggle;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key.toLowerCase() !== 'f' || e.metaKey || e.ctrlKey || e.altKey || el.closest('input, textarea, select, [contenteditable], [role=dialog]')) return;
      e.preventDefault();
      toggleFs();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggleFs]);

  const pickIndex = (id: string) => { setIndex(id); setSector(null); replay.live(); };
  const today = journal.entries.filter((e) => e.date === date);
  const movers = snap ? moverList(view ?? snap, list) : [];

  return (
    <div className={explain ? 'explain' : undefined}>
      <AppHeader section="flow" mode={mode} status={status} user={user} role={role} query={query} strip={strip} activeIndex={index} onSelectIndex={pickIndex} />
      <Page>
        {/* Phones: one line of subtitle, and the controls in one row (Explain and Full screen as icons). */}
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 max-sm:gap-y-2">
          <div className="min-w-0">
            <h1 className="text-[26px] font-semibold max-sm:text-[22px]">Market Flow</h1>
            <p className="mt-0.5 text-sm text-muted-foreground max-sm:truncate max-sm:text-[13px]">
              {live && status?.clock?.state === 'holiday'
                ? <>Where money moved in the last session, sector by sector · {date} · <HolidayTag clock={status.clock} session={status.date} className="align-middle" /></>
                : <><span className="max-sm:hidden">Where money is moving today, sector by sector</span><span className="sm:hidden">Sector by sector</span> · {date}{live ? '' : ` · simulated session #${seed}`}</>}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 max-sm:w-full max-sm:flex-nowrap max-sm:gap-2">
            {live && firstTimer && (
              <Button className="h-9 rounded-full px-4" onClick={() => openDesk(GUIDE_START)}>
                <DeskIcon /> New here? Your first paper trade, step by step
              </Button>
            )}
            <div className="min-w-0 max-sm:flex-1 [&>div]:max-sm:w-full"><ReplayBar r={replay} time={snap?.time ?? '—'} /></div>
            {!live && (
              <Button variant="outline" className="h-9 rounded-full px-4" onClick={() => { setSeed(Math.floor(Math.random() * 1e8)); replay.live(); }}>
                <RefreshCwIcon /> New session
              </Button>
            )}
            <Button variant={explain ? 'default' : 'outline'} className="h-9 shrink-0 rounded-full px-4 max-sm:w-9 max-sm:px-0" aria-pressed={explain} aria-label="Explain" title="Explain" onClick={() => setExplain((x) => !x)}>
              <LightbulbIcon /><span className="max-sm:hidden">Explain</span>
            </Button>
            <Button variant="outline" className="h-9 shrink-0 rounded-full px-4 max-sm:w-9 max-sm:px-0" onClick={toggleFs} aria-label="Full screen" title="Full screen (F)">
              <Maximize2Icon /><span className="max-sm:hidden">Full screen</span>
            </Button>
          </div>
        </div>

        <div className="mt-5 mb-3 flex flex-wrap items-center gap-3 max-sm:mt-3 max-sm:gap-x-2 max-sm:gap-y-2">
          <IndexSelect value={index} onChange={pickIndex} />
          <IndexQuote session={session} snap={snap} mode={mode} status={status} name={ix.name} />
          {snap && <SessionBand t={tt} n={replay.n} date={date} />}
          {activeSector && (
            <button type="button" title="Show all sectors (Esc)" onClick={() => setSector(null)}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-primary bg-brand-soft px-3 text-[13px] font-medium text-brand-strong">
              {activeSector} · {view?.stocks.length} stocks <XIcon className="size-3.5" />
            </button>
          )}
        </div>

        <div ref={fsRef} className={cn(fs && 'flex h-dvh flex-col gap-2 bg-tint p-3', full.cover)}>
          {full.turnHint && <TurnHint />}
          {fs && (
            <div className="flex flex-wrap items-center gap-3 px-1">
              <Logo size={24} />
              <span className="font-heading text-[15px] font-semibold text-ink">Market Flow</span>
              <IndexQuote session={session} snap={snap} mode={mode} status={status} name={ix.name} />
              {activeSector && (
                <button type="button" title="Show all sectors" onClick={() => setSector(null)}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-primary bg-brand-soft px-3 text-[13px] font-medium text-brand-strong">
                  {activeSector} · {view?.stocks.length} stocks <XIcon className="size-3.5" />
                </button>
              )}
              <span className="flex-1" />
              <ReplayBar r={replay} time={snap?.time ?? '—'} />
              <Button variant={explain ? 'default' : 'outline'} className="h-9 rounded-full px-4" aria-pressed={explain} onClick={() => setExplain((x) => !x)}>
                <LightbulbIcon /> Explain
              </Button>
              <Button variant="outline" className="h-9 rounded-full px-4" onClick={toggleFs} title="Exit full screen (Esc)">
                <Minimize2Icon /> Exit
              </Button>
            </div>
          )}
          {/* The board scrolls sideways on phones; it keeps that to itself (no chaining into the page)
              and leaves vertical swipes to the page. */}
          <Card className={cn('gap-0 p-1.5', fs ? 'min-h-0 flex-1 overflow-hidden' : 'touch-pan-x touch-pan-y overflow-x-auto overflow-y-hidden overscroll-x-contain')}>
            <div ref={boxRef} className={cn(fs && 'h-full w-full')}>
            <svg id="board" ref={svgRef} role="img" onMouseLeave={() => setTip(null)}
              className={cn('block', fs ? 'h-full w-full' : 'h-auto w-full min-w-[1100px]')}
              aria-label="Market flow board: sector breadth, intraday stock paths, strength ranking and top 20 bullish and bearish stocks" />
            </div>
          </Card>
          {/* Inside the full-screen element, or it would be hidden behind it. */}
          {tip && <BoardTooltip tip={tip} board={board} snap={snap} forming={forming} />}
        </div>

        <RecentlyViewed bySymbol={bySymbol} href={stockHref} />

        <div className="mt-8 grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-semibold">Top movers{activeSector ? ` in ${activeSector}` : ''}</h2>
              <Link href={`/movers${query}`} className="inline-flex items-center gap-1 text-sm font-medium text-brand-strong hover:underline">
                See all <ArrowRightIcon className="size-4" />
              </Link>
            </div>
            <Pills label="Mover list" value={list} onChange={setList} options={MOVER_LISTS.map((m) => ({ id: m.id, label: m.label }))} className="mb-3" />
            <Card className="gap-0 py-0">
              <MoversTable rows={movers.slice(0, 8)} t={tt} href={stockHref} compact
                empty={list === 'flow' ? 'Order flow is measured from live ticks, so it appears once the live feed is running.' : undefined} />
            </Card>
          </section>
          <aside className="grid gap-5">
            <ShortlistCard entries={today} bySymbol={bySymbol} href={stockHref} query={query} />
            <HowToCard />
          </aside>
        </div>

        <Explainer />
      </Page>
    </div>
  );
}

/** Index value from Kite when live, else the members' equal-weight average move. */
/** Which part of the day the board shows: opening noise, prime time, the midday slowdown... */
function SessionBand({ t, n, date }: { t: number; n: number; date: string }) {
  // After the last bar the day is over; otherwise the clock is at the end of bar t.
  const minute = t >= NSE.bars ? 24 * 60 : NSE.openMinute + t * NSE.barMinutes;
  const ph = sessionPhase(minute, date >= '2026-08-03');
  const tone = ph.id === 'prime' || ph.id === 'afternoon' ? 'border-bull-line bg-bull-soft text-bull-strong'
    : ph.id === 'closed' || ph.id === 'pre' ? 'border-border bg-card text-muted-foreground'
    : 'border-warn-line bg-warn-soft text-warn-ink';
  return (
    <span title={ph.note} className={cn('inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium', tone)}>
      <ClockIcon className="size-3.5" /> {ph.label}{t < n && ph.id !== 'closed' ? ' (replay)' : ''}
      <span className="sr-only">: {ph.note}</span>
    </span>
  );
}

function IndexQuote({ session, snap, mode, status, name }: {
  session: Session | null; snap: Snapshot | null; mode: Mode; status: FeedStatus | null; name: string;
}) {
  const q = session?.index;
  const ex = status?.extra;
  const short = q ? q.members - q.loaded : 0;
  let body: React.ReactNode = null, title = '';
  if (q && q.ltp && q.prevClose) {
    body = <><span className="num text-ink">{fmtPx(q.ltp)}</span> <Change pct={(q.ltp / q.prevClose - 1) * 100} /></>;
    title = 'Live index value';
  } else if (snap?.stocks.length) {
    body = <>avg <Change pct={snap.stocks.reduce((x, s) => x + s.pct, 0) / snap.stocks.length} /></>;
    title = 'Equal-weight average move of the members (the index value itself needs live data)';
  }
  if (!body) return null;
  return (
    <span className="index-quote text-sm text-muted-foreground" title={title}>
      <b className="font-heading font-semibold text-ink">{name}</b> {body}
      {mode !== 'sim' && ex?.phase === 'backfill' && short > 0 && <span className="text-brand-strong"> · loading {ex.done}/{ex.total} index stocks</span>}
    </span>
  );
}

/** The stocks the user opened lately (on any device), with today's move where the board has it. */
function RecentlyViewed({ bySymbol, href }: { bySymbol: Map<string, Analysed>; href: (s: string) => string }) {
  const recents = useRecents().slice(0, 12);
  useEffect(refreshRecents, []); // today's prices for stocks outside the board's index
  if (!recents.length) return null;
  return (
    <section className="mt-8">
      <h2 className="mb-3 text-xl font-semibold">Recently viewed</h2>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] lg:mx-0 lg:flex-wrap lg:px-0">
        {recents.map(({ symbol: sym, price, changePct }) => {
          // The board's own figure when it has the stock (live), else the list's (any stock).
          const b = bySymbol.get(sym);
          const s = b ? { ltp: b.ltp, pct: b.pct } : price != null && changePct != null ? { ltp: price, pct: changePct } : null;
          return (
            <Link key={sym} href={href(sym)} className="flex flex-none items-center gap-2.5 rounded-xl border bg-card py-2 pr-3.5 pl-2 hover:bg-tint">
              <Initials symbol={sym} size="sm" />
              <div className="min-w-0">
                <div className="text-[13.5px] font-medium text-ink">{sym}</div>
                {s ? <div className="flex items-baseline gap-1.5 text-xs"><span className="num text-muted-foreground">{NSE.currencySymbol}{fmtPx(s.ltp)}</span><Change pct={s.pct} className="text-xs" /></div>
                  : <div className="max-w-36 truncate text-xs text-muted-foreground">{NSE_NAMES[sym] ?? 'Open'}</div>}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function ShortlistCard({ entries, bySymbol, href, query }: {
  entries: ReturnType<typeof useJournal>['entries']; bySymbol: Map<string, Analysed>; href: (s: string) => string; query: string;
}) {
  return (
    <Card className="gap-0 py-0">
      <CardHeader className="flex items-center justify-between border-b py-4">
        <CardTitle className="text-[15px] font-semibold">Your shortlist <span className="font-normal text-muted-foreground">· today</span></CardTitle>
        <Link href={`/shortlist${query}`} className="text-sm font-medium text-brand-strong hover:underline">Open</Link>
      </CardHeader>
      {entries.length ? entries.slice(0, 6).map((e) => {
        const now = bySymbol.get(e.symbol);
        const s = now ? since(e, now.ltp) : null;
        return (
          <Link key={e.id} href={href(e.symbol)} className="flex items-center gap-3 border-b border-border/60 px-4 py-3 last:border-b-0 hover:bg-tint">
            <Initials symbol={e.symbol} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-ink">{e.symbol}</div>
              <div className="text-xs text-muted-foreground">{e.side === 'bull' ? 'Bullish' : 'Bearish'} · added {e.time}</div>
            </div>
            {s ? <span className={cn('num text-[13px] font-medium', s.good ? 'text-bull' : s.cls === 'bad' ? 'text-bear' : 'text-muted-foreground')}>{fmtPct(s.chg)}</span>
              : <span className="text-xs text-muted-foreground">not in index</span>}
          </Link>
        );
      }) : (
        <CardContent className="py-6 text-sm text-muted-foreground">
          <StarIcon className="mb-2 size-5 text-primary" />
          Open a stock and press <b className="text-ink">Add to shortlist</b>. Write down why, then review it after the close.
        </CardContent>
      )}
    </Card>
  );
}

/** How a zone reads in the hover card: fresh, testing, reacted, no reaction, broken (and visits). */
const zoneWord = (o: Analysed['obs'][number]) => {
  const st = zoneState(o), n = o.touches.length;
  const w = st === 'failed' ? 'no reaction' : st;
  return n > 1 && st !== 'broken' ? `${w} · ${n} visits` : w;
};

const HOW_SHORT = [
  { icon: LayersIcon, title: 'Sector ladder', text: 'Strongest to weakest sector. Click one to filter the board.' },
  { icon: WavesIcon, title: 'Money-flow lines', text: 'Each line is one stock’s path since 09:15.' },
  { icon: BoxIcon, title: 'Order blocks', text: 'Dashed boxes: likely footprints of large orders.' },
  { icon: TriangleIcon, title: 'Liquidity sweeps', text: 'Amber marks: stops triggered past a level, then rejected.' },
  { icon: GaugeIcon, title: 'Strength 0–100', text: 'Move size, volume, VWAP hold and open interest combined.' },
];

function HowToCard() {
  return (
    <Card className="gap-0 py-0">
      <CardHeader className="flex items-center justify-between border-b py-4">
        <CardTitle className="text-[15px] font-semibold">How to read the board</CardTitle>
        <a href="#how" className="text-sm font-medium text-brand-strong hover:underline">More</a>
      </CardHeader>
      {HOW_SHORT.map((h) => (
        <div key={h.title} className="flex items-start gap-3 border-b border-border/60 px-4 py-3 last:border-b-0">
          <span className="grid size-8 flex-none place-items-center rounded-lg bg-brand-soft text-primary"><h.icon className="size-4" /></span>
          <div>
            <div className="text-[13.5px] font-semibold text-ink">{h.title}</div>
            <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{h.text}</p>
          </div>
        </div>
      ))}
    </Card>
  );
}

function BoardTooltip({ tip, board, snap, forming }: { tip: Tip; board: Session | null; snap: Snapshot | null; forming: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<SVGSVGElement>(null);
  const theme = useResolvedTheme();
  const [pos, setPos] = useState({ left: tip.x + 14, top: tip.y + 14 });
  const s = tip.s;
  const raw = board?.stocks.find((x) => x.symbol === s.symbol);
  const d = useMemo(() => (raw && snap ? detail(raw, s, snap.t, snap.stocks, { lastBarForming: forming }) : null), [raw, s, snap, forming]);
  useEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    let left = tip.x + 14, top = tip.y + 14;
    if (left + r.width > window.innerWidth - 8) left = tip.x - r.width - 14;
    if (top + r.height > window.innerHeight - 8) top = Math.max(8, window.innerHeight - r.height - 8);
    setPos({ left, top });
  }, [tip.x, tip.y, d]);
  useEffect(() => {
    if (chartRef.current && d && snap) drawStockChart(chartRef.current, NSE, s, d, snap.t, { compact: true });
  }, [s, d, snap, theme]);

  const up = s.bull;
  const side = up ? 'text-bull' : 'text-bear';
  const chg = s.ltp - s.prevClose;
  const p = d?.plan;
  const zone = d?.zone;
  const dots = Math.max(1, Math.round(s.score / 20));
  const sideCount = snap ? snap[up ? 'bulls' : 'bears'].length : 0;
  const cell = (k: string, v: React.ReactNode, cls?: string) => (
    <div className="flex items-baseline justify-between gap-2 border-b border-border/60 py-1 last:border-b-0">
      <dt className="text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">{k}</dt>
      <dd className={cn('num text-right text-[11.5px] text-ink', cls)}>{v}</dd>
    </div>
  );
  const flow = s.flow;
  return (
    <div ref={ref} style={pos} className="tip pointer-events-none fixed z-50 w-[440px] rounded-2xl border bg-card p-4 text-xs text-ink shadow-[0_24px_60px_rgba(11,23,34,0.22)]">
      <div className="flex items-center gap-3">
        <Initials symbol={s.symbol} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="font-heading text-[16px] leading-tight font-semibold">{s.symbol}</div>
          <div className="truncate text-[11px] text-muted-foreground">{NSE_NAMES[s.symbol] ?? s.symbol} · {s.sector}</div>
        </div>
        {raw && snap && <Sparkline series={raw.bars.slice(0, snap.t).map((b) => b.c)} up={up} width={96} height={30} />}
      </div>
      <div className="num mt-2 flex items-baseline justify-between">
        <b className="text-[17px] font-semibold">{cur}{fmtPx(s.ltp)}</b>
        <span className={cn('text-[12.5px] font-medium', side)}>{fmtPct(s.pct)} · {chg >= 0 ? '+' : '−'}{fmtPx(Math.abs(chg))}</span>
      </div>
      {s.eventRisk && (
        <div className="mt-2 rounded-lg border border-warn-line bg-warn-soft px-2.5 py-1.5 text-[11.5px] leading-snug text-warn-ink"><b>Event risk:</b> {s.eventRisk}</div>
      )}
      <dl className="mt-2 grid grid-cols-2 gap-x-5 border-t pt-1.5">
        <div>
          {cell('Rank', d ? `P${d.percentile.toFixed(1)} · #${tip.rank}/${sideCount}` : `#${tip.rank}`)}
          {cell('OI', s.hasOI ? `${fmtPct(s.oiChg)} · ${s.buildUp}` : 'no futures')}
          {cell('VWAP', `${fmtPx(s.vwap)} · ${s.ltp >= s.vwap ? 'above' : 'below'}`)}
          {cell('Z / Stop', p ? `${fmtPx(p.z)} · ${fmtPx(p.stop)}` : '—')}
          {cell('R:R', p ? (p.rr === null ? 'no target' : `1:${p.rr >= 5 ? '5+' : p.rr.toFixed(1)}`) : d ? `1:${d.rr.toFixed(1)} now` : '—', p?.rr != null && p.rr >= 2 ? 'text-bull' : undefined)}
        </div>
        <div>
          {cell('Strength', <>{s.score} <span className={side}>{'●'.repeat(dots)}<span className="opacity-25">{'●'.repeat(5 - dots)}</span></span></>)}
          {cell('Flow Δ', flow ? `${flow.deltaPct >= 0 ? '+' : '−'}${Math.abs(flow.deltaPct).toFixed(0)}% ${flow.deltaPct >= 0 ? 'buy' : 'sell'}` : `vol ${s.relVol.toFixed(1)}×`)}
          {cell('OB', zone ? <>{fmtPx(zone.bottom)}–{fmtPx(zone.top)} <span className="text-muted-foreground">{zoneWord(zone)}</span></> : 'none yet')}
          {cell(up ? 'SH1 / SH2' : 'SL1 / SL2', d ? `${fmtPx(up ? d.levels.R1 : d.levels.S1)} · ${fmtPx(up ? d.levels.R2 : d.levels.S2)}` : '—')}
          {cell('Sector', `${s.withSector ? 'with' : 'alone'} · ${fmtPct(s.sectorAvg)}`)}
        </div>
      </dl>
      {d && <svg ref={chartRef} className="dt-chart mt-2 rounded-lg border bg-card" role="img" aria-label={`${s.symbol} intraday candles with zones and levels`} />}
      <p className="mt-2 text-[11.5px] leading-snug text-muted-foreground">{story(s)}</p>
    </div>
  );
}

const HOW = [
  { icon: ListOrderedIcon, title: 'Index', text: 'Pick a universe at the top: the full F&O list (default), a broad index (NIFTY 50, NEXT 50, 100, 500, midcap, smallcap) or a sector index (BANK, IT, PHARMA…). Everything, including ranks, sectors and leaderboards, is calculated within that index.' },
  { icon: BarChart3Icon, title: 'Breadth strip', text: 'Each small square is one stock, placed by how much it moved today (−4% to +4%). Rings are sectors. The counter shows how many stocks are up versus down: a quick read on whether the whole market is being bought or sold.' },
  { icon: LayersIcon, title: 'Sector ladder', text: 'Sectors ranked strongest to weakest. The striped bar is the share of a sector’s stocks that are rising. Money rotates between sectors, and this is where you see it. Click a sector to see only its stocks; click again or press Esc to show all.' },
  { icon: WavesIcon, title: 'Money-flow lines', text: 'Each line is one stock’s journey from 09:15 to now. Green finished up, red down. The vertical scale stretches where stocks are crowded, so small moves stay readable.' },
  { icon: BoxIcon, title: 'Order blocks', text: 'Dashed boxes mark the last opposite candle before a sharp move that closes beyond a recent swing high or low: a possible footprint of a large buyer or seller. Price may react when it returns (about 4 in 10 first visits did on recent NSE data). Once price closes through it, the zone is broken (hatched). Click a zone on a stock chart to see why it qualifies.' },
  { icon: GaugeIcon, title: 'Strength column', text: 'Bar length is a 0–100 score blending move size, volume versus normal, closing near the high/low, holding above/below VWAP, and whether open interest confirms new positions. Brackets mark the 20 strongest on each side.' },
  { icon: ShuffleIcon, title: 'Sector mark', text: 'The small S on each card is filled when the stock moves with its sector and hollow when it moves alone. Strength compares each move with the stock’s usual daily range, so 2% counts for more in a slow stock than a volatile one.' },
  { icon: TriangleIcon, title: 'Liquidity sweeps', text: 'Amber triangles (and SW↑/SW↓ on cards) mark where price poked past an obvious level — yesterday’s high/low, the first 15 minutes’ range, equal highs or lows, or the day’s earlier high/low — triggered the stops there, then closed back inside. A swept high that fails is bearish; a swept low that recovers is bullish.' },
  { icon: LineChartIcon, title: 'Order flow (estimated)', text: 'On a stock’s page the Δ panel shows buyer- versus seller-initiated volume per 5-minute bar, and the line is the running total. It is estimated from live ticks, so it starts when the feed starts. When flow disagrees with price, large passive orders are often absorbing the move.' },
  { icon: StarIcon, title: 'Leaderboards', text: 'Top 20 bullish and bearish stocks; bigger card = higher rank. Inside each card: a volume profile, dark bands for order blocks and a dashed line for the current price. Hover a card for a plain-English read; click it to open the stock.' },
];

function Explainer() {
  return (
    <section id="how" className="mt-12 scroll-mt-40">
      <h2 className="text-xl font-semibold">How to read this board</h2>
      <p className="mt-1 text-sm text-muted-foreground">Ten ideas, top to bottom. Press <b className="text-ink">Explain</b> to number them on the board.</p>
      <div className="mt-4 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
        {HOW.map((h, i) => (
          <Card key={h.title} className="gap-2 px-5 py-4.5">
            <h3 className="flex items-center gap-2 text-[15px] font-semibold">
              <span className="grid size-7 place-items-center rounded-lg bg-brand-soft text-primary"><h.icon className="size-4" /></span>
              <span className="text-muted-foreground">{i + 1}.</span> {h.title}
            </h3>
            <p className="text-[13px] leading-relaxed text-muted-foreground">{h.text}</p>
          </Card>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground/80">For learning only, not investment advice. Real NSE data: live in market hours, otherwise the last closed session.</p>
    </section>
  );
}
